import type { TFunction } from 'i18next';
import { flow } from 'lodash';
import type { FC, ReactElement } from 'react';
import { connect } from 'react-redux';

import { AuthActions } from 'common/auth/actions';
import { Form } from 'common/component/ValidationFormAndField';
import withTranslate from 'common/i18n/i18n';
import { mustMatch, required, validPassword } from 'common/utils/validations';
import { PasswordValidIndicators } from 'common/viewcomp/display/PasswordValidIndicators';
import { CITextField } from 'common/viewcomp/forms/fields/text';

interface Props {
    t: TFunction;
    onSubmit: () => void;
    submitting: boolean;
    children: (disableButton) => ReactElement;
    changePasswordRequest: (newpass) => void;
}

/**
 * Used in User registration flow, for creating password for first time.
 * Used in Patient password reset flow for setting new password.
 */
const UpdatePasswordFormComponent: FC<Props> = (props): ReactElement => {
    const { t, onSubmit, submitting, changePasswordRequest, children } = props;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSubmit = ({ newpass, confirmpass }): void => {
        changePasswordRequest(newpass);
        onSubmit();
    };

    return (
        <Form
            t={t}
            onSubmit={handleSubmit}
            initialValues={{ newpass: '', confirmpass: '' }}
            validations={[mustMatch('newpass', 'confirmpass', 'confirmpass', 'password_form.must_match')]}
            render={({ handleSubmit, invalid, values: { newpass } }): ReactElement => {
                const disableButton = submitting || invalid;

                return (
                    <form id="updatePassForm" onSubmit={handleSubmit}>
                        <CITextField
                            t={t}
                            id="newpass"
                            name="newpass"
                            labelI18NKey="password_form.newpass"
                            type="password"
                            margin="normal"
                            fullWidth
                            trimWhiteSpaces={false}
                            required
                            maxLength={16} // temporary until specs are defined
                            validations={[required('error.required'), validPassword('error.invalid_password')]}
                            autoFocus
                        />

                        <PasswordValidIndicators password={newpass} t={t} />

                        <CITextField
                            t={t}
                            id="confirmpass"
                            name="confirmpass"
                            labelI18NKey="password_form.confirm_pass"
                            type="password"
                            margin="normal"
                            trimWhiteSpaces={false}
                            fullWidth
                            maxLength={16} // temporary until specs are defined
                            required
                            validations={[required('error.required')]}
                        />

                        {/* Childrens are buttons to be rendered with the this component
                        this makes button rendering more flexible, naming and maybe cancel button etc etc */}
                        {children(disableButton)}
                    </form>
                );
            }}
        />
    );
};

const mapDispatchToProps = { ...AuthActions };

const UpdatePasswordForm = flow([withTranslate('auth')])(
    connect(null, mapDispatchToProps)(UpdatePasswordFormComponent)
);

export default UpdatePasswordForm;
